export type AppLocale = 'en' | 'fr';

const appTranslations: Record<AppLocale, Record<string, any>> = {
    en: {
        common: {
            decision: 'Decision',
            undefined: 'Undefined',
            unknownError: 'An unknown error occured',
            unknownUser: 'Unknown user',
            by: 'by',
            to: 'to',
            permissionError: 'Permission denied',
            back: 'Back',
            yes: 'Yes',
            no: 'No',
            empty: 'Empty',
            confirmDialogTitle: 'Confirm',
            confirmDeletion: 'Confirm deletion?',
            deletionError: 'Error during deletion',
            deletionSuccessful: 'Deletion successful',
            edit: 'Edit',
            view: 'View',
            privacyPolicy: 'Privacy policy',
            close: 'Close',
        },
        status: {
            NOT_STARTED: 'Not started',
            ONGOING: 'Ongoing',
            TO_REVIEW: 'To review',
            CHECKING: 'Checking',
            APPROVED: 'Approved',
            // Display-only timeline markers, not workflow statuses.
            UPLOADED_BY: 'Uploaded',
            ASSIGNED_TO: 'Assigned',
        },
        login: {
            email: 'Email',
            password: 'Password',
            login: 'Log in',
            forgotPassword: 'Forgot password?',
            errors: {
                missingCredentials: 'Please fill in both fields',
                wrongCredentials: 'Your email/password combination is incorrect',
            },
        },
        header: {
            administration: 'Administration',
            logout: 'Log out',
            home: 'Home',
            litigations: 'Litigations',
            changePassword: 'Change Password',
        },
        listAnalyses: {
            analyses: 'Analyses',
            decision: '@:common.decision',
            uploadDate: 'Added on',
            lastModifiedDate: 'Last modified',
            analyzedBy: 'Analyzed by',
            status: 'Status',
            addDecision: 'Add decision',
            exportAnalyses: 'Export analyses',
            export: 'Export',
            exportCentralization: 'Export centralization',
            exportMonotony: 'Export monotony',
            fileName: 'File name',
            multipleAnalysesTooltip: 'Multiple analyses exist for this decision.',
            foremostCriteria: 'Foremost criteria',
            errorCount: 'Errors',
            week: '{week} week',
            search: 'Search...',
        },
        bulkApprove: {
            approve: 'Approve',
            title: 'Approve analyses ({count} selected)',
            selectAll: 'Select all',
            confirm: 'Confirm ({count})',
            reviewTitle: 'Confirm approval',
            reviewHeading: 'You are about to approve {count} analyses',
            reviewIntro: 'Review the analyses below, then choose Approve to continue.',
            confirmAction: 'Approve',
            cancel: 'Cancel',
            loadError: 'Unable to load analyses to approve.',
            allApprovedTitle: 'All analyses approved',
            allApproved: 'All {count} selected analyses were approved successfully.',
            partialTitle: 'Approval completed with issues',
            partialSummary: '{approved} approved, {failed} could not be approved. Review the reasons below.',
            submitError: 'Approval failed due to a server error. Please try again.',
            status: 'Status',
            approvedStatus: 'Approved',
            done: 'Done',
            reasons: {
                not_found: 'Analysis no longer exists.',
                not_authorized: 'You do not have permission to approve this analysis.',
                unprocessed_errors: 'Unresolved errors were found; the analysis was returned to checking.',
            },
            reasonsShort: {
                not_found: 'Not found',
                not_authorized: 'Not authorized',
                unprocessed_errors: 'Errors found',
            },
        },
        addDecision: {
            decisionAddition: 'Decision addition',
            noFile: 'No file was provided',
            success: 'Decision uploaded',
            title: 'Add decision',
            assignTo: 'Assign to',
            assignToDisabledHint: 'Uploaded decisions are assigned to you.',
            dropFilesHint: 'Drop files here or click to select',
            acceptedTypesHint: 'Accepted: {types}',
            submit: 'Upload',
            cancel: 'Cancel',
            uploading: 'Uploading…',
            allUploaded: '{count} decision(s) uploaded and assigned.',
            partialUploaded: '{success} uploaded, {failed} failed. Review the files below and retry.',
            allFailed: 'No decisions could be uploaded. Please try again.',
            retryFailed: 'Retry failed',
            loadAnalystsError: 'Could not load the analyst list. You can still upload decisions assigned to yourself.',
            remove: 'Remove',
            status: {
                ready: 'Ready',
                uploading: 'Uploading…',
                success: 'Uploaded',
                error: 'Failed',
            },
        },
        duplicateDecision: {
            title: 'Similar analysis already exists',
            warning:
                'The following uploaded decisions already have other analyses for the same decision in this module. Review them below and delete any upload you do not want to keep.',
            deleteUpload: 'Delete this decision',
            viewSimilar: 'View similar decisions ({count})',
        },
        analysis: {
            decision: '@:common.decision',
            criteria: 'Criteria',
            check: 'Check',
            checkError: 'Please fix errors in your analysis',
            checkSuccess: 'Your analysis is now complete',
            flagForReview: 'Flag for review',
            flagForReviewSuccess: 'Your analysis will be reviewed by your supervisor',
            approve: 'Approve',
            approveSuccess: 'Analysis approved',
            contextSelectionButtonOn: 'Highlight text',
            contextSelectionButtonOff: 'Deactivate highlighter',
            tooltipSelectionButtonOn: 'Activate tooltips',
            tooltipSelectionButtonOff: 'Deactivate tooltips',
            deleteAnalysis: 'Delete analysis',
            deleteConfirmTitle: 'Warning',
            deleteConfirmMessage: 'Confirm deletion?',
            deleteConfirmed: 'Analysis deleted',
            deleteCanceled: 'Deletion canceled',
            annotate: 'Annotate',
            rawTextTab: 'Raw text',
            pdfTab: 'PDF',
            criteriaTab: 'Criteria',
            statusesTab: 'History',
            similarAnalysesTab: 'Similar analyses',
            similarAnalysesHint: 'Other analyses exist for the same decision in this module.',
            similarAnalysesEmpty: 'No other analysis for this decision.',
            fixAction: 'Mark as fixed',
            ignoreAction: 'Ignore error',
            invalidDate: 'Invalid date',
        },
        listMembers: {
            members: 'Members',
            showMoreMembers: 'Show more members',
            addMember: 'Add member',
            cancel: 'Cancel',
            create: 'Create',
            email: 'Email',
            name: 'Name',
            firstName: 'First name',
            lastName: 'Last name',
            isEnabled: 'Enabled',
            litigations: 'Litigations',
            role: 'Role',
            roles: {
                user: 'Analyst',
                expert: 'Expert Analyst',
                supervisor: 'Supervisor',
                admin: 'Administrator',
            },
            memberCreated: 'New member successfully created',
        },
        listLitigations: {
            litigations: 'Litigations',
            code: 'Code',
            addLitigation: 'Add litigation',
            cancel: 'Cancel',
            create: 'Create',
        },
        memberDetailsForm: {
            security: 'Security',
            requirePasswordResetHelp: 'The member must set a new password on their next login.',
            requirePasswordReset: 'Reset password',
            requirePasswordResetConfirmTitle: 'Reset password',
            requirePasswordResetConfirmMessage: 'Are you sure you want to reset the password for {email}?',
            saving: 'Saving…',
            saved: 'All changes saved',
            saveError: 'Could not save changes',
        },
        litigationForm: {
            pageTitle: 'Edit',
            save: 'Save',
            newCriterion: 'New',
            code: 'Code',
            title: 'Title',
            description: 'Description',
            criteria: 'Criteria',
        },
        criterionForm: {
            createTitle: 'New',
            editTitle: 'Edit',
            general: 'General',
            type: 'Type',
            input: 'Input',
            output: 'Output',
            none: 'None',
            code: 'Code',
            codeHelp: `- Allowed characters: lowercase letters, numbers and "_" as word separator<br>
            - Starts with a letter<br>
            - 3 characters minimum<br>
            <br>
            If this criterion appears several times in this module (as in MPT or LITIDESIGN), you can indicate the number of repetitions here`,
            label: 'Label',
            warningLength: 'Pay attention to the length of the label',
            control: 'Control',
            warningDate:
                "If you don't enter a max date, then the max date will be the current date at the time of analysis.",
            minimum: 'Minimum',
            maximum: 'Maximum',
            switch: 'Yes / No',
            dropdownSingle: 'Single dropdown list',
            dropdownMultiple: 'Multiple dropdown list',
            range: 'Range',
            integer: 'Integer',
            float: 'Float',
            date: 'Date',
            text: 'Text',
            url: 'URL',
            unit: 'Unit',
            days: 'days',
            months: 'months',
            years: 'years',
            choices: 'Choices',
            warningChoices: `The -1 choice is implicit, so there's no need to enter it here; it will be added automatically to the analysis page.<br />
            <br />
            Choices will be automatically sorted in ascending code order when saved.<br />
            <br />
            Example:<br />
            <pre>
1: Choice 1
2: Choice 2</pre>`,
            suggestions: 'Suggestions',
            warningSuggestions: `One suggestion per line. Analysts will see these as autocomplete options but can still type a custom value.<br />
            <br />
            Example:<br />
            <pre>
Paris
Lyon
Marseille</pre>`,
            section: 'Section',
            warningSection: `If you want the sections to appear in a particular order, you can prefix them with a letter.<br />
            Example:<br />
            <pre>A - General</pre>`,
            help: 'Help',
            required: 'Required',
            rules: 'Rules',
            errors: {
                required: 'The criterion #criterion# is required',
                range: 'The criterion #criterion# must be between #min# and #max#',
                list: 'The criterion #criterion# must belong to #choices#',
                url: 'The criterion #criterion# contains an invalid URL',
                combinations: 'The criterion #criterion# does not respect the authorized #combinations# combinations',
                suggestions: 'The criterion #criterion# must be one of the following values: #suggestions#',
                from: 'from',
                to: 'to',
                or: 'or',
                rules: {
                    si_contient_alors_different:
                        'If the criterion #criterion# contains #value#, then the criterion #criterion# must be different than #value#',
                    si_contient_alors_egal:
                        'If the criterion #criterion# contains #value#, then the criterion #criterion# must be equal to #value#',
                    si_contient_alors_rempli:
                        'If the criterion #criterion# contains #value#, then the criterion #criterion# must be filled',
                    si_different_alors_contient:
                        'If the criterion #criterion# is different than #value#, then the criterion #criterion# must contain #value#',
                    si_different_alors_egal:
                        'If the criterion #criterion# is different than #value#, then the criterion #criterion# must be equal to #value#',
                    si_egal_alors_contient:
                        'If the criterion #criterion# equals to #value#, then the criterion #criterion# must contain #value#',
                    si_egal_alors_different:
                        'If the criterion #criterion# equals to #value#, then the criterion #criterion# must be different than #value#',
                    si_egal_alors_egal:
                        'If the criterion #criterion# equals to #value#, then the criterion #criterion# must be equal to #value#',
                    si_egal_alors_rempli:
                        'If the criterion #criterion# equals to #value#, then the criterion #criterion# must be filled',
                    si_egal_alors_vide:
                        'If the criterion #criterion# equals to #value#, then the criterion #criterion# must be empty',
                    si_rempli_alors_rempli:
                        'If the criterion #criterion# is filled, then the criterion #criterion# must be filled',
                    si_rempli_alors_superieur:
                        'If the criterion #criterion# is filled, then the criterion #criterion# must be superior',
                    si_vide_alors_rempli:
                        'If the criterion #criterion# is empty, then the criterion #criterion# must be filled',
                },
            },
            newRule: 'New rule',
            ruleExample: `Example:<br />
            <pre>0;3</pre>
            If more than one value, separate them with semicolons.`,
            combinations: 'Combinations',
            combinationsWarning: 'At least one of the following combinations must be respected',
            orOnly: 'or only',
            combinationExemple: `Example:<br />
            <pre>0;3</pre>
            If more than one value, separate them with semicolons.`,
            minValue: 'Min value',
            maxValue: 'Max value',
            newCombination: 'New combination',
            litigation: 'Litigation',
            jpControl: 'JP control',
            example: 'Example data',
            quickEdit: 'Quick edit',
            aiConfig: 'AI config',
            noiseIntensity: 'Noise intensity',
            lowerThreshold: 'Lower threshold',
            upperThreshold: 'Upper threshold',
            monotony: 'Monotony',
            monotoneAugmentationAmplitude: 'Amplitude',
            monotoneAugmentationAmplitudeHelp: `Maximum increase or decrease in criterion value :<br>
                in absolute value if it is to be an integer<br>
                or relatively if it is to be a percentage<br>
                (e.g. IPLSCRS: the number of employees can be increased by 10% (i.e. amplitude = 0.10) when generating decisions, while the age can be increased by 2 (i.e. amplitude = 2.00) when generating decisions)`,
            monotoneAugmentationMin: 'Min',
            monotoneAugmentationMinHelp: 'Minimum value for decision generation',
            monotoneAugmentationMax: 'Max',
            monotoneAugmentationMaxHelp: 'Maximum value for decision generation',
            notConsidered: 'Not considered',
            descending: 'Descending',
            neutral: 'Neutral',
            ascending: 'Ascending',
            request: 'Request',
            proposal: 'Proposal',
            submit: 'Save',
            submitAndNew: 'Save and create a new one',
            invalidFormat: 'Invalid format',
            contextualData: 'Contextual Data (LLM)',
            contextualDataHelp:
                'This field allows you to transmit to the machine the directives and context necessary for building its reasoning chain.',
            contextualDataPlaceholder: 'Enter any instructions or context for the LLM...',
        },
        passwordView: {
            title: 'This password will only be shown once. Please copy and store it securely or send it to the relevant user.',
            subtext: 'The user will be prompted to set a new password immediately after logging in.',
        },
        resetPassword: {
            subtitle: 'You must set a new password before continuing.',
            newPassword: 'New password',
            confirmPassword: 'Confirm new password',
            submit: 'Set new password',
            success: 'Your password has been updated successfully.',
            errors: {
                required: 'Please enter a new password.',
                strength:
                    'Password must be at least 12 characters and contain uppercase, lowercase, a number, and a special character.',
                mismatch: 'Passwords do not match.',
                failed: 'Failed to update password. Please try again.',
            },
        },
    },
    fr: {
        common: {
            decision: 'Décision',
            undefined: 'Non défini',
            unknownError: 'Une erreur est survenue',
            unknownUser: 'Utilisateur inconnu',
            by: 'par',
            to: 'à',
            permissionError: 'Action non autorisée',
            back: 'Retour',
            yes: 'Oui',
            no: 'Non',
            empty: 'Vide',
            confirmDialogTitle: 'Confirmation',
            confirmDeletion: 'Confirmer la suppression ?',
            deletionError: 'Erreur lors de la suppression',
            deletionSuccessful: 'Suppression effectuée',
            edit: 'Editer',
            view: 'Voir',
            privacyPolicy: 'Politique de confidentialité et données personnelles',
            close: 'Fermer',
        },
        status: {
            NOT_STARTED: 'Pas commencée',
            ONGOING: 'En cours',
            TO_REVIEW: 'À revoir',
            CHECKING: 'En vérification',
            APPROVED: 'Approuvée',
            // Marqueurs d'affichage uniquement, pas des statuts du workflow.
            UPLOADED_BY: 'Déposée',
            ASSIGNED_TO: 'Assignée',
        },
        login: {
            email: 'Email',
            password: 'Mot de passe',
            login: 'Se connecter',
            forgotPassword: 'Mot de passe oublié ?',
            errors: {
                missingCredentials: 'Merci de remplir les deux champs',
                wrongCredentials: "L'email ou le mot de passe sont incorrects",
            },
        },
        header: {
            administration: 'Administration',
            logout: 'Se déconnecter',
            home: 'Accueil',
            litigations: 'Contentieux',
            changePassword: 'Changer le mot de passe',
        },
        listAnalyses: {
            analyses: 'Analyses',
            decision: '@:common.decision',
            uploadDate: "Date d'ajout",
            lastModifiedDate: 'Date de dernière modification',
            analyzedBy: 'Analysée par',
            status: 'Statut',
            addDecision: 'Ajouter décision',
            exportAnalyses: 'Exporter les analyses',
            export: 'Exporter',
            exportCentralization: 'Exporter centralisation',
            exportMonotony: 'Exporter monotonie',
            fileName: 'Nom du fichier',
            multipleAnalysesTooltip: 'Plusieurs analyses existent pour cette décision.',
            foremostCriteria: 'Critères significatifs',
            errorCount: 'Erreurs',
            week: 'Semaine du {week}',
            search: 'Rechercher...',
        },
        bulkApprove: {
            approve: 'Approuver',
            title: 'Approuver les analyses ({count} sélectionnées)',
            selectAll: 'Tout sélectionner',
            confirm: 'Confirmer ({count})',
            reviewTitle: "Confirmer l'approbation",
            reviewHeading: 'Vous êtes sur le point d’approuver {count} analyses',
            reviewIntro: 'Vérifiez les analyses ci-dessous, puis choisissez Approuver pour continuer.',
            confirmAction: 'Approuver',
            cancel: 'Annuler',
            loadError: 'Impossible de charger les analyses à approuver.',
            allApprovedTitle: 'Toutes les analyses approuvées',
            allApproved: 'Les {count} analyses sélectionnées ont été approuvées avec succès.',
            partialTitle: 'Approbation terminée avec des problèmes',
            partialSummary:
                "{approved} approuvées, {failed} n'ont pas pu être approuvées. Consultez les raisons ci-dessous.",
            submitError: "L'approbation a échoué en raison d'une erreur serveur. Veuillez réessayer.",
            status: 'Statut',
            approvedStatus: 'Approuvée',
            done: 'Terminé',
            reasons: {
                not_found: "L'analyse n'existe plus.",
                not_authorized: "Vous n'avez pas la permission d'approuver cette analyse.",
                unprocessed_errors:
                    "Des erreurs non résolues ont été détectées ; l'analyse est repassée en vérification.",
            },
            reasonsShort: {
                not_found: 'Introuvable',
                not_authorized: 'Non autorisée',
                unprocessed_errors: 'Erreurs détectées',
            },
        },
        addDecision: {
            decisionAddition: 'Ajout de décision',
            noFile: "Aucun fichier n'a été fourni",
            success: 'Décision envoyée',
            title: 'Ajouter décision',
            assignTo: 'Assigner à',
            assignToDisabledHint: 'Les décisions envoyées vous sont assignées.',
            dropFilesHint: 'Déposez les fichiers ici ou cliquez pour sélectionner',
            acceptedTypesHint: 'Acceptés : {types}',
            submit: 'Envoyer',
            cancel: 'Annuler',
            uploading: 'Envoi en cours…',
            allUploaded: '{count} décision(s) envoyée(s) et assignée(s).',
            partialUploaded: '{success} envoyée(s), {failed} en échec. Vérifiez les fichiers ci-dessous et réessayez.',
            allFailed: "Aucune décision n'a pu être envoyée. Veuillez réessayer.",
            retryFailed: 'Réessayer les échecs',
            loadAnalystsError:
                'Impossible de charger la liste des analystes. Vous pouvez tout de même envoyer des décisions qui vous sont assignées.',
            remove: 'Retirer',
            status: {
                ready: 'Prêt',
                uploading: 'Envoi…',
                success: 'Envoyé',
                error: 'Échec',
            },
        },
        duplicateDecision: {
            title: 'Une analyse similaire existe déjà',
            warning:
                'Les décisions envoyées ci-dessous ont déjà d’autres analyses pour la même décision dans ce module. Vérifiez-les et supprimez tout envoi que vous ne souhaitez pas conserver.',
            deleteUpload: 'Supprimer cette décision',
            viewSimilar: 'Voir les décisions similaires ({count})',
        },
        analysis: {
            decision: '@:common.decision',
            criteria: 'Critères',
            check: 'Vérifier',
            checkError: 'Merci de corriger les erreurs présentes dans votre analyse',
            checkSuccess: 'Votre analyse est terminée',
            flagForReview: 'Marquer comme "à revoir"',
            flagForReviewSuccess: 'Votre analyse va être revue par votre superviseur',
            approve: 'Approuver',
            approveSuccess: 'Analyse approuvée',
            contextSelectionButtonOn: 'Surligner du texte',
            contextSelectionButtonOff: 'Désactiver le surligneur',
            tooltipSelectionButtonOn: 'Activer les infobulles',
            tooltipSelectionButtonOff: 'Désactiver les infobulles',
            deleteAnalysis: "Supprimer l'analyse",
            deleteConfirmTitle: 'Attention',
            deleteConfirmMessage: 'Confirmer la suppression ?',
            deleteConfirmed: 'Analyse supprimée',
            deleteCanceled: 'Suppression annulée',
            annotate: 'Annoter',
            rawTextTab: 'Texte brut',
            pdfTab: 'PDF',
            criteriaTab: 'Critères',
            statusesTab: 'Historique',
            similarAnalysesTab: 'Analyses similaires',
            similarAnalysesHint: 'D’autres analyses existent pour la même décision dans ce module.',
            similarAnalysesEmpty: 'Aucune autre analyse pour cette décision.',
            fixAction: 'Marquer comme corrigé',
            ignoreAction: "Ignorer l'erreur",
            invalidDate: 'Date invalide',
        },
        listMembers: {
            members: 'Utilisateurs',
            showMoreMembers: 'Afficher plus de membres',
            addMember: 'Ajouter utilisateur',
            cancel: 'Annuler',
            create: 'Créer',
            email: 'Email',
            name: 'Nom',
            firstName: 'Prénom',
            lastName: 'Nom',
            isEnabled: 'Actif',
            litigations: 'Contentieux',
            role: 'Rôle',
            roles: {
                user: 'Analyste',
                expert: 'Analyste Expert',
                supervisor: 'Superviseur',
                admin: 'Administrateur',
            },
            memberCreated: 'Nouvel utilisateur créé avec succès',
        },
        listLitigations: {
            litigations: 'Contentieux',
            code: 'Code',
            addLitigation: 'Ajouter contentieux',
            cancel: 'Annuler',
            create: 'Créer',
        },
        memberDetailsForm: {
            security: 'Sécurité',
            requirePasswordResetHelp: 'L’utilisateur devra définir un nouveau mot de passe à sa prochaine connexion.',
            requirePasswordReset: 'Réinitialiser le mot de passe',
            requirePasswordResetConfirmTitle: 'Réinitialiser le mot de passe',
            requirePasswordResetConfirmMessage: 'Êtes-vous sûr de vouloir réinitialiser le mot de passe de {email} ?',
            saving: 'Enregistrement…',
            saved: 'Modifications enregistrées',
            saveError: 'Impossible d’enregistrer les modifications',
        },
        litigationForm: {
            pageTitle: 'Edition',
            save: 'Enregistrer',
            newCriterion: 'Nouveau',
            code: 'Code',
            title: 'Titre',
            description: 'Description',
            criteria: 'Critères',
        },
        criterionForm: {
            createTitle: 'Nouveau',
            editTitle: 'Edition',
            general: 'Général',
            type: 'Type',
            input: 'Entrée',
            output: 'Sortie',
            none: 'Aucun des deux',
            code: 'Code',
            codeHelp: `- Caractères autorisés : lettres minuscules, chiffres et "_" comme séparateur de mot<br>
            - Commence par une lettre<br>
            - 3 caractères minimum<br>
            <br>
            Si ce critère est présent plusieurs fois dans ce module (c'est le cas des postes sur MPT ou des modèles sur LITIDESIGN), vous pouvez indiquer ici le nombre de répétitions`,
            label: 'Libellé',
            warningLength: 'Attention à la longueur du libellé',
            control: 'Contrôle',
            warningDate:
                "Si vous ne saisissez pas de date max, alors la date max sera la date du jour lors de l'analyse",
            minimum: 'Minimum',
            maximum: 'Maximum',
            switch: 'Oui / Non',
            dropdownSingle: 'Liste à choix unique',
            dropdownMultiple: 'Liste à choix multiple',
            range: 'Intervalle de nombres',
            integer: 'Nombre entier',
            float: 'Nombre décimal',
            date: 'Date',
            text: 'Texte',
            url: 'URL',
            unit: 'Unité',
            days: 'jours',
            months: 'mois',
            years: 'ans',
            choices: 'Choix liste',
            warningChoices: `Le choix -1 est implicite, il est inutile de le saisir ici, il sera ajouté
            automatiquement dans la page d'analyse.<br />
            <br />
            Les choix seront ordonnés automatiquement par ordre croissant de code à la sauvegarde.<br />
            <br />
            Exemple :<br />
            <pre>
1: Choix 1
2: Choix 2</pre>`,
            suggestions: 'Suggestions',
            warningSuggestions: `Une suggestion par ligne. Les analystes verront ces valeurs en autocomplétion mais peuvent saisir une valeur libre.<br />
            <br />
            Exemple :<br />
            <pre>
Paris
Lyon
Marseille</pre>`,
            section: 'Section',
            warningSection: `Si vous souhaitez faire apparaître les sections dans un ordre particulier, vous
            pouvez les préfixer par une lettre<br />
            Exemple :<br />
            <pre>A - Généralités</pre>`,
            help: 'Aide',
            required: 'Obligatoire',
            rules: 'Règles',
            errors: {
                required: 'Le critère #criterion# doit être rempli',
                range: 'Le critère #criterion# doit être compris entre #min# et #max#',
                list: 'Le critère #criterion# doit appartenir à #choices#',
                url: 'Le critère #criterion# contient une URL invalide',
                combinations: 'Le critère #criterion# ne respecte pas les combinaisons autorisées #combinations#',
                suggestions: 'Le critère #criterion# doit avoir une des valeurs suivantes : #suggestions#',
                from: 'de',
                to: 'à',
                or: 'ou',
                rules: {
                    si_contient_alors_different:
                        'Si le critère #criterion# contient #value#, alors le critère #criterion# doit être différent de #value#',
                    si_contient_alors_egal:
                        'Si le critère #criterion# contient #value#, alors le critère #criterion# doit être égal à #value#',
                    si_contient_alors_rempli:
                        'Si le critère #criterion# contient #value#, alors le critère #criterion# doit être rempli',
                    si_different_alors_contient:
                        'Si le critère #criterion# est différent de #value#, alors le critère #criterion# doit contenir #value#',
                    si_different_alors_egal:
                        'Si le critère #criterion# est différent de #value#, alors le critère #criterion# doit être égal à #value#',
                    si_egal_alors_contient:
                        'Si le critère #criterion# est égal à #value#, alors le critère #criterion# doit contenir #value#',
                    si_egal_alors_different:
                        'Si le critère #criterion# est égal à #value#, alors le critère #criterion# doit être différent de #value#',
                    si_egal_alors_egal:
                        'Si le critère #criterion# est égal à #value#, alors le critère #criterion# doit être égal à #value#',
                    si_egal_alors_rempli:
                        'Si le critère #criterion# est égal à #value#, alors le critère #criterion# doit être rempli',
                    si_egal_alors_vide:
                        'Si le critère #criterion# est égal à #value#, alors le critère #criterion# doit être vide',
                    si_rempli_alors_rempli:
                        'Si le critère #criterion# est rempli, alors le critère #criterion# doit être rempli',
                    si_rempli_alors_superieur:
                        'Si le critère #criterion# est rempli, alors le critère #criterion# doit être supérieur',
                    si_vide_alors_rempli:
                        'Si le critère #criterion# est vide, alors le critère #criterion# doit être rempli',
                },
            },
            newRule: 'Nouvelle règle',
            ruleExample: `Exemple :<br />
            <pre>0;3</pre>
            Si plusieurs valeurs, alors il faut les séparer par des points virgules`,
            combinations: 'Combinaisons',
            combinationsWarning: 'Au moins une des combinaisons suivantes devra être respectée',
            orOnly: 'ou uniquement',
            combinationExemple: `Exemple :<br />
            <pre>0;3</pre>
            Si plusieurs valeurs, alors il faut les séparer par des points virgules`,
            minValue: 'Valeur min',
            maxValue: 'Valeur max',
            newCombination: 'Nouvelle combinaison',
            litigation: 'Contentieux',
            jpControl: 'Contrôle JP',
            example: 'Donnée exemple',
            quickEdit: 'Edition rapide',
            aiConfig: 'Configuration moteurs',
            noiseIntensity: 'Intensité bruit',
            lowerThreshold: 'Seuil bas bruit',
            upperThreshold: 'Seuil haut bruit',
            monotony: 'Monotonie',
            monotoneAugmentationAmplitude: 'Amplitude',
            monotoneAugmentationAmplitudeHelp: `Augmentation ou diminution maximale de la valeur du critère :<br>
                en valeur absolue s’il doit s’agir d’un entier<br>
                ou de façon relative s’il doit s’agir d’un pourcentage<br>
                (ex IPLSCRS : le nombre de salarié peut être augmenté de 10% (donc amplitude = 0.10) lors de la génération de décisions tandis que l’âge peut être augmenté de 2 (donc amplitude = 2.00) lors de la génération de décisions)`,
            monotoneAugmentationMin: 'Plancher',
            monotoneAugmentationMinHelp: 'Valeur minimale lors de la génération de décisions',
            monotoneAugmentationMax: 'Plafond',
            monotoneAugmentationMaxHelp: 'Valeur maximale lors de la génération de décisions',
            notConsidered: 'Non pris en compte',
            descending: 'Décroissant',
            neutral: 'Neutre',
            ascending: 'Croissant',
            request: 'Demande',
            proposal: 'Proposition',
            submit: 'Enregistrer',
            submitAndNew: 'Enregistrer et créer un nouveau',
            invalidFormat: 'Format invalide',
            contextualData: 'Données contextuelles (LLM)',
            contextualDataHelp:
                'Ce champ permet de transmettre à la machine les directives et le contexte nécessaires à la construction de sa chaîne de raisonnement.',
            contextualDataPlaceholder: 'Entrez les directives ou le contexte pour le LLM...',
        },
        passwordView: {
            title: "Ce mot de passe ne sera affiché qu'une seule fois. Veuillez le copier et le stocker en toute sécurité ou l'envoyer à l'utilisateur concerné.",
            subtext: "L'utilisateur sera invité à définir un nouveau mot de passe immédiatement après s'être connecté.",
        },
        resetPassword: {
            subtitle: 'Vous devez définir un nouveau mot de passe avant de continuer.',
            newPassword: 'Nouveau mot de passe',
            confirmPassword: 'Confirmer le nouveau mot de passe',
            submit: 'Définir le nouveau mot de passe',
            success: 'Votre mot de passe a été mis à jour avec succès.',
            errors: {
                required: 'Veuillez saisir un nouveau mot de passe.',
                strength:
                    'Le mot de passe doit contenir au moins 12 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial.',
                mismatch: 'Les mots de passe ne correspondent pas.',
                failed: 'Échec de la mise à jour du mot de passe. Veuillez réessayer.',
            },
        },
    },
};

export const guessLocale = (rawRequestedLocale: string): AppLocale => {
    const defaultLocale: AppLocale = 'en';
    const requestedLocale = rawRequestedLocale.substring(0, 2);
    const locale: AppLocale = requestedLocale in appTranslations ? (requestedLocale as AppLocale) : defaultLocale;

    return locale;
};

export const getTranslationsByLocale = (locale: AppLocale): Record<string, any> => appTranslations[locale];

export default appTranslations;
